import React, { useEffect, useRef } from 'react';
import { DOTS, DOT_MAP_H, DOT_MAP_W, DOT_X_MAX, DOT_X_MIN, DOT_Y_MIN } from './dotMapData';

/**
 * 움직이는 점 지도 (Dashboard Command 패널 배경) — 시안 animated_dot_world_map 과 같은 계산 · 같은 화면 크기.
 * 격자 위 점은 그대로 있고, 천천히 떠다니는 빛 3개가 지나가는 자리의 점이 커진다.
 * 시안 설정: Speed 470% · Glow 118% · Base 32% · Max 30% · Dot size 155% · Size contrast 140% · Blur 0%.
 * 점 · 빛의 화면 크기는 시안이 화면 가득 그려질 때(점 간격 약 5.4px)와 같게 둔다 — 지도 확대와 상관없이.
 * 지도 가장자리의 점은 서서히 흐려진다 (땅이 잘린 경계가 네모로 보이지 않게). 동작 줄이기면 한 장만 그리고 멈춘다.
 *
 *   view  { w, h, s, ox, oy } — 패널 크기(px)와 지도 좌표(1600 × 1100) → 패널 좌표 (x * s + ox, y * s + oy)
 */

const SPEED = 4.7;
const GLOW = 1.18;
const BASE = 0.32;
const MAX = 0.3;
const DOT_SIZE = 1.55;
const CONTRAST = 1.4;
const BLUR = 0;
/** 전체 밝기 — 시안보다 10% 어둡게 */
const DIM = 0.9;
/** 시안이 화면 가득 그려질 때 원본 1칸이 몇 px 인지 (원본 점 간격 7 → 화면 약 5.4px) */
const SCREEN = 0.771;
/** 시안의 빛 크기 (원본 단위) — [가로, 세로] */
const LIGHT_SIZE = [
  [290, 190],
  [310, 205],
  [310, 205],
];

const W = DOT_MAP_W;
const H = DOT_MAP_H;
/** 점 지도 가장자리에서 점이 사라지기까지의 폭 (지도 좌표) — 땅이 잘린 경계가 네모로 보이지 않게 */
const EDGE_FADE = 160;

export const draw = (ctx, view, t) => {
  const { w, h, s, ox, oy } = view;
  ctx.clearRect(0, 0, w, h);
  // 시안과 같은 빛 3개 — 지도 가로 23% · 51% · 78% 언저리를 천천히 떠다닌다
  const centers = [
    [W * (0.23 + 0.1 * Math.sin(t * 0.19)), H * (0.39 + 0.12 * Math.cos(t * 0.23))],
    [W * (0.51 + 0.11 * Math.sin(t * 0.17 + 2.1)), H * (0.51 + 0.13 * Math.sin(t * 0.21 + 0.5))],
    [W * (0.78 + 0.1 * Math.cos(t * 0.16 + 1.1)), H * (0.43 + 0.12 * Math.sin(t * 0.2 + 2.4))],
  ];
  // 빛의 크기는 화면 기준 — 지도 좌표로 바꾼다
  const lights = centers.map(([lx, ly], i) => [
    lx,
    ly,
    (LIGHT_SIZE[i][0] * SCREEN) / s,
    (LIGHT_SIZE[i][1] * SCREEN) / s,
  ]);
  // 화면 밖 점은 건너뛴다 (조금 넉넉하게)
  const minX = -ox / s - 10;
  const maxX = (w - ox) / s + 10;
  const minY = -oy / s - 10;
  const maxY = (h - oy) / s + 10;
  for (let i = 0; i < DOTS.length; i += 3) {
    const x = DOTS[i];
    const y = DOTS[i + 1];
    if (x < minX || x > maxX || y < minY || y > maxY) continue;
    const r = DOTS[i + 2];
    let field = 0;
    for (let j = 0; j < lights.length; j += 1) {
      const l = lights[j];
      const dx = (x - l[0]) / l[2];
      const dy = (y - l[1]) / l[3];
      field += Math.exp(-2.25 * (dx * dx + dy * dy)) * 0.95;
    }
    // 시안의 반짝임은 원본 좌표 기준 — 화면 크기로 맞춘다
    const sx = (x * s) / SCREEN;
    const sy = (y * s) / SCREEN;
    const shimmer = 0.5 + 0.5 * Math.sin(t * 0.64 + sx * 0.009 - sy * 0.006);
    // 점 지도 가장자리 가까운 점은 서서히 흐리게 (양옆 · 위쪽은 이어 붙인 땅의 바깥 끝)
    const edge = Math.min(x - DOT_X_MIN, DOT_X_MAX - x, y - DOT_Y_MIN, H - y);
    const fade = Math.min(1, Math.max(0, edge / EDGE_FADE));
    const a =
      Math.min(MAX, BASE + GLOW * (0.1 * shimmer + 0.72 * Math.min(field, 1.2))) * fade * fade * DIM;
    const activation = Math.max(0, Math.min(1, (field * GLOW) / 1.3));
    const radius = r * 1.18 * DOT_SIZE * (1 + activation * CONTRAST) * SCREEN;
    const cx = x * s + ox;
    const cy = y * s + oy;
    // 빛이 닿은 점 둘레의 번짐 — 시안과 같은 계산
    const blurAmount = BLUR * activation;
    if (blurAmount > 0.001) {
      ctx.fillStyle = `rgba(255,255,255,${Math.min(0.18, a * 0.17) * blurAmount})`;
      ctx.beginPath();
      ctx.arc(cx, cy, radius * (1.4 + blurAmount * 1.3), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = `rgba(255,255,255,${a})`;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();
  }
};

const DotMap = ({ view }) => {
  const ref = useRef(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !view) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(view.w * dpr);
    canvas.height = Math.round(view.h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let time = 0;
    let last = 0;
    let raf = 0;
    const frame = (now) => {
      if (!last) last = now;
      time += Math.min((now - last) / 1000, 0.05) * SPEED;
      last = now;
      draw(ctx, viewRef.current, time);
      raf = window.requestAnimationFrame(frame);
    };
    if (reduce) draw(ctx, view, 0);
    else raf = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(raf);
    // 패널 크기가 바뀌면 캔버스를 다시 잡는다 (위치만 바뀌면 viewRef 로 다음 프레임에 반영)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view?.w, view?.h]);

  if (!view) return null;
  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{ position: 'absolute', left: 0, top: 0, width: view.w, height: view.h, pointerEvents: 'none' }}
    />
  );
};

export default DotMap;
